import EventBus from '../services/service.eventBus';
import { fetchInProgressOffer, fetchOfferDetail, submitAnotherOffer, submitOffer } from '../services/service.offer';
import { resetAll } from '../store/store.all';
import { state as answerState } from '../store/store.answers';
import { state as customerState } from '../store/store.customer';
import { state as dealerState } from '../store/store.dealer';
import { state as offerState } from '../store/store.offer';
import { state as optimizelyState } from '../store/store.optimizely';
import { state as offerVehicleState } from '../store/store.offer-vehicle';
import { state as pageParamsState } from '../store/store.page-params';
import { state as validationState } from '../store/store.validation';
import { state as kelleyBuysYourCarOfferState } from '../store/store.kelley-buys-your-car-offer';
import { state as funnelComponentState } from '../store/store.funnel-component';
// Messaging
import { trackOfferCertExpiredResubmit, trackOfferCertificateHeader, trackOfferCertLoad, trackOfferIneligible } from '../tracking/tracking.offer';
import { formatCurrency } from '../utils/utils.currency';
import { calculateDealerBoost, isUnderReview, TRACKED_OFFER_STATUSES, DR_BUTTON_TEXT } from '../utils/utils.offer';
import { QUESTION_ARE_QUESTIONS_VERIFIED } from '../utils/utils.question';
import { toISOStringOrNull } from '../utils/utils.time-format';
import { MinimalCertificateDisplayType } from '../interfaces/interface.minimal-certificate-offer';

import answerActions from './actions.answers';
import autocheckActions from './actions.autocheck';
import customerActions from './actions.customer';
import dealerActions from './actions.dealer';
import { autocheckIsIneligible } from './actions.eligibility';
import offerVehicleActions from './actions.offer-vehicle';
import questionActions from './actions.questions';
import counterofferActions from './actions.counteroffer';

import { disposalOptions } from '../interfaces/interface.offer';
import { onConsumerMarketConfidenceInit } from './actions.consumer-market-confidence';

export const onOfferFetch = async (offerID: string, isTrackOfferCertLoad: boolean = true, isKelleyBuysYourCar: boolean = false): Promise<void> => {
  EventBus.publish('OfferFetch', offerID);

  try {
    const offer = await fetchOfferDetail(offerID);

    if (shouldUpdateOfferState(offer)) {
      onOfferFetchSuccess(offer, isKelleyBuysYourCar);
      isTrackOfferCertLoad && trackOfferCertLoad();
    }
    EventBus.publish('OfferLoadComplete', null);
  } catch (error) {
    console.error('offer fetch fail', error);
    onOfferFetchFail(error);
  }
};

export const onInProgressOfferFetch = async (prospectId: string): Promise<void> => {
  EventBus.publish('InProgressOffer', prospectId);

  try {
    const inProgressOffer = await fetchInProgressOffer(prospectId);
    onInProgressOfferFetchSuccess(prospectId, inProgressOffer);
    trackOfferCertLoad();
    EventBus.publish('InProgressOfferLoadComplete', null);
  } catch (error) {
    console.error('inProgress offer fetch fail', error);
    onInProgressOfferFetchFail(error);
  }
};

// For offers in Further Review, we refresh the offer every few seconds to see if it has been reviewed by a KBB rep
// This is causing several re renders of the certificate component even when the offer data hasnt changed
// To avoid unnecessary re renders this function has been added to check if the offer is in FR and INSPECTION,
// compare the current offer state to the newly loaded offer and only update state if there has been a change
// We should only compare offer states if the offer is in Further Review and INSPECTION. All other offer types should refresh state regardless of changes
// INSPECTION status is included in the check on the scenario where the status changes from FR -> INSPECTION, INSPECTION status should not call unnecesary functions. INSPECTION status also uses the offer-further-review offer certificate
const shouldUpdateOfferState = (offer): boolean => {
  if (offer.status !== 'FR' && offer.status !== 'INSPECTION') {
    return true;
  }

  return (
    offerState.amount !== offer.amount ||
    offerState.accidentQuestionModifiedByDealer !== offer.accidentQuestionModifiedByDealer ||
    offerState.channelId !== offer.channelId ||
    offerState.dealerId !== offer.prospect.dealerId ||
    offerState.dealerBoostAmount !== calculateDealerBoost(offer.markupAmounts) ||
    offerState.expirationDate !== new Date(offer.expirationDate).toLocaleDateString() ||
    offerState.isExpired !== offer.isExpired ||
    offerState.offerId !== offer.offerId ||
    offerState.status !== offer.status ||
    offerState.offerImages.length !== offer.offerImages.length ||
    offerState.appointmentsEnabled !== offer.isAppointmentRequestEnabled ||
    offerState.potentialTaxSaving !== offer.potentialTaxSaving ||
    offerState.primaryBodyStyle !== offer.primaryBodyStyle ||
    offerState.currentWeeklyDepreciation !== offer.currentWeeklyDepreciation
  );
};
const onOfferFetchSuccess = (offer, isKelleyBuysYourCar: boolean = false): void => {
  const { offerId, amount, status, expirationDate, offerCode, markupAmounts, totalOfferAmounts } = offer;
  EventBus.publish('OfferFetchSuccess', {
    offerId,
    amount,
    status,
    expirationDate,
    offerCode,
    markupAmounts,
    totalOfferAmounts,
  });

  // Go and wipe out the existing answers data so it doesn't hang around
  console.log('Clearing saved answers and resetting stores on successful offer fetch');
  answerActions.clearAnswers();
  resetAll();
  // Sets offer state
  onOfferSet({ ...offer, isKelleyBuysYourCar });
  // Dispatch to additional entitites
  /***
    Autocheck will always be called on offer certificate whether or not the user answers the history questions (if autocheck was down or turned off)
  ***/
  if (!offerState.isFurtherReviewNeeded) {
    autocheckActions.onOfferLoad(offer);
  }
  customerActions.onOfferLoad(offer.prospect);
  dealerActions.onOfferLoad(offer);
  questionActions.onOfferLoad(offer);
  onConsumerMarketConfidenceInit();
  offerState.isAssignedOffer || offerState.isDrOffer ? offerVehicleActions.onOfferLoadWithoutVehicleImage(offer) : offerVehicleActions.onOfferLoadWithVehicleImage(offer);

  // Track offer certificate load only for statuses 'FR' (Further Review) and 'INSPECTION'
  if (TRACKED_OFFER_STATUSES.includes(status)) {
    trackOfferCertLoad();
  }
};

const onInProgressOfferFetchSuccess = (prospectId, inProgressOffer): void => {
  EventBus.publish('InProgressOfferFetchSuccess', inProgressOffer);
  offerState.prospectId = prospectId;
  customerActions.onOfferLoad(inProgressOffer);
  offerVehicleActions.onInProgressVehicleLoad(inProgressOffer.vehicle);
};

const onInProgressOfferFetchFail = (error): void => {
  EventBus.publish('InProgressOfferFetchFail', error);
};

const onOfferFetchFail = (error): void => {
  EventBus.publish('OfferFetchFail', error);
};

const onOfferStatusCheck = (offerId): void => {
  EventBus.publish('OfferStatusCheck', offerId);
  onOfferFetch(offerId);
};

// Sets offer state
const onOfferSet = ({
  accidentQuestionModifiedByDealer,
  amount,
  channelId,
  expirationDate,
  isExpired,
  isAppointmentRequestEnabled,
  markupAmounts,
  offerId,
  offerImages = [],
  status,
  prospect,
  offerCode,
  potentialTaxSaving,
  primaryBodyStyle,
  canBeResubmitted,
  acceptOrCounteroffer,
  ineligibleReasons,
  currentWeeklyDepreciation,
  isKelleyBuysYourCar,
  kbycOffset,
  kbycDisposalInfo,
  disposalType,
  kbbOptionAdjustment,
  valuation,
  conditionTierSelection,
}) => {
  offerState.amount = amount;
  offerState.accidentQuestionModifiedByDealer = accidentQuestionModifiedByDealer;
  offerState.channelId = channelId;
  offerState.dealerId = prospect.dealerId;
  offerState.dealerBoostAmount = calculateDealerBoost(markupAmounts);
  offerState.expirationDate = new Date(expirationDate).toLocaleDateString();
  offerState.originalExpirationDate = expirationDate;
  offerState.isAssignedOffer = prospect.dealerId !== 0;
  offerState.isDrOffer = offerCode === 'D';
  offerState.isExpired = isExpired;
  offerState.offerId = offerId;
  offerState.status = status;
  offerState.offerImages = offerImages;
  offerState.appointmentsEnabled = isAppointmentRequestEnabled;
  offerState.isCobrandingAssigned = offerCode === 'WC' || offerCode === 'TC';
  offerState.isCobrandingUnassigned = offerCode === 'OC';
  offerState.isOfficeClosed = status === 'kbbReviewNBH';
  offerState.isOfferUnderReview = isUnderReview(status);
  offerState.isIneligibleOffer = status === 'INELIGIBLE';
  offerState.isFurtherReviewNeeded = status === 'FR';
  offerState.isInspectionNeeded = status === 'INSPECTION';
  offerState.formattedFinalOfferAmount = getFormattedFinalOfferAmount(amount, offerState.dealerBoostAmount, kbycOffset, isKelleyBuysYourCar);
  offerState.offerCode = offerCode;
  offerState.offerPriceDrButtonText =
    funnelComponentState.isSuperComponent && dealerState.isDealerCobrandingDrEnabled ? DR_BUTTON_TEXT.CONTINUE : DR_BUTTON_TEXT.COMPLETE_YOUR_TRADE_IN;
  offerState.potentialTaxSaving = potentialTaxSaving;
  offerState.currentWeeklyDepreciation = currentWeeklyDepreciation;
  offerState.primaryBodyStyle = primaryBodyStyle;
  offerState.canBeResubmitted = canBeResubmitted;
  counterofferActions.onUpdateFromOffer(acceptOrCounteroffer);
  offerState.showDealerChosenReasons = !optimizelyState.optimizedleadrouting;
  offerState.ineligibleReasons = ineligibleReasons;
  offerState.isKelleyBuysYourCar = isKelleyBuysYourCar;
  kelleyBuysYourCarOfferState.isDisposalAccepted = kbycDisposalInfo?.disposalAcceptedDate !== undefined;
  kelleyBuysYourCarOfferState.offerId = offerId;
  offerState.disposalType = disposalType;
  offerState.kbbOptionAdjustment = kbbOptionAdjustment;
  offerState.valuation = valuation;
  offerState.conditionTierSelection = conditionTierSelection;
  setOfferCertificateDisplayFlags();
};

const getFormattedFinalOfferAmount = (amount: number, dealerBoostAmount: number, kbycOffset: number, isKelleyBuysYourCar: boolean): string => {
  if (isKelleyBuysYourCar) {
    return formatCurrency(amount + kbycOffset);
  }

  if (dealerBoostAmount > 0) {
    return formatCurrency(amount + dealerBoostAmount);
  }
  return formatCurrency(amount);
};

export const setOfferCertificateDisplayFlags = () => {
  offerState.isCobrandingDRUnassigned = dealerState.isDealerCobrandingDrEnabled && offerState.isCobrandingUnassigned;
  offerState.isCobrandingDRAssigned = dealerState.isDealerCobrandingDrEnabled && offerState.isCobrandingAssigned;
  offerState.showCustomerIneligibleNextSteps =
    !offerState.isAssignedOffer && !offerState.isDrOffer && !offerState.isCobrandingUnassigned && !offerState.isCobrandingDRUnassigned && !pageParamsState.isAdmin;
  offerState.showCustomerDealerList = !offerState.isDrOffer && !offerState.isCobrandingDRUnassigned && !offerState.isCobrandingDRAssigned && !pageParamsState.isAdmin;
  offerState.showDealerListForPrint = !offerState.isDrOffer && !offerState.isCobrandingDRUnassigned && !offerState.isCobrandingDRAssigned;
  offerState.useTradeIn = offerState.isDrOffer || offerState.isCobrandingDRUnassigned || offerState.isCobrandingDRAssigned;
  offerState.displayRequestAppointmentButton = !offerState.isDrOffer && !offerState.isCobrandingDRAssigned && !offerState.isCobrandingDRUnassigned && !pageParamsState.isAdmin;
  offerState.tradeInBtnText = getTradeInButtonText();
  offerState.showIcoQuestionsSection = offerState.offerCode && offerState.offerCode.toUpperCase() !== 'TC';
  offerState.showOfferPriceButton = offerState.offerCode && offerState.offerCode.toUpperCase() !== 'TC';
  offerState.showOfferPriceDRButton = offerState.isDrOffer || dealerState.isDealerCobrandingDrEnabled;
};

const getTradeInButtonText = (): string => {
  const { isDealerCobrandingDrEnabled } = dealerState;
  const { isSuperComponent } = funnelComponentState;

  if (isDealerCobrandingDrEnabled) {
    return isSuperComponent ? DR_BUTTON_TEXT.CONTINUE : DR_BUTTON_TEXT.RETURN_TO_DEAL;
  }
  return DR_BUTTON_TEXT.USE_TRADE_IN;
};

export const setMinimalCertificateDisplayFlags = (displayType: MinimalCertificateDisplayType): void => {
  offerState.minimalCertificateDisplayType = displayType;
};

export const setDealerListDisplay = (showDealerList: boolean) => {
  if (offerState.showDealerListForPrint) offerState.showDealerList = showDealerList;
};

export const onSendDealerOfferSuccess = (dealerId): void => {
  offerState.sentToDealers = [...offerState.sentToDealers, dealerId];

  EventBus.publish('SendDealerOfferSuccess', dealerId);
};

export const onSendDealerOfferFailure = (error): void => {
  EventBus.publish('SendDealerOfferFailure', error);
};

export const onUpdateOfferIneligible = (canEditMileageIneligible, minimumMileageIneligibleThreshold) => {
  offerState.canEditMileageIneligible = canEditMileageIneligible;
  offerState.minimumMileageIneligibleThreshold = minimumMileageIneligibleThreshold;
  offerState.enableMileageIneligibleEditing = pageParamsState.isAdmin && offerState.canEditMileageIneligible;
};

// //
// //
// / DON"T DO THIS WILL FIX IN FUTURE
// /
// /
//
export const onOfferResubmit = (): void => {
  const vehicleData = {
    mileage: offerVehicleState.mileage,
    editMileageIneligibleOffer: offerState.enableMileageIneligibleEditing,
  };

  EventBus.publish('OfferResubmit', vehicleData);
  trackOfferCertExpiredResubmit();
};

export const onUpdateOfferInfo = (): void => {
  const vehicleData = {
    year: { displayName: offerVehicleState.year },
    make: { displayName: offerVehicleState.make },
    model: { displayName: offerVehicleState.model },
    mileage: offerVehicleState.mileage,
  };
  EventBus.publish('onUpdateOfferInfo', vehicleData);
  trackOfferCertExpiredResubmit();
};

export const onOfferSubmit = (clearStateOnFailure = true, allowIneligibleVehicleSubmission = false, disposalOptions: disposalOptions = null) => {
  EventBus.publish('OfferSubmit', {});
  // if disposalOptions are passed in, we are on the Unified Disposer flow
  const { existingKbycOfferId = null, existingOfferId = null } = disposalOptions || {};

  // if there are no existing offer IDs from KBYC or ICO, submit the offer with both ICO and KBYC

  // if an existing offerId exists for ICO or KBYC, do the offer "tack on" logic (adding another offer ontop of an existing one)
  // since ICO and KBYC share the same offer id, we just return a new response with both ICO and KBYC data
  if (existingOfferId || existingKbycOfferId) {
    submitAnotherOffer(disposalOptions)
      .then((res) => {
        onOfferSubmitSuccess(res, disposalOptions);
      })
      .catch((e) => {
        if (e.errors) {
          if (onValidateResponse(e.errors)) {
            EventBus.publish('OfferSubmitError', e);
          } else if (e.errors.includes(400123)) {
            EventBus.publish('OfferInProgress', e.data);
          } else {
            onOfferSubmitError(e, clearStateOnFailure);
          }
        } else {
          onOfferSubmitFailure(e, clearStateOnFailure);
        }
      });
  }
  // check if vehicle is eligible and if autocheck check is turned on and if allowIneligibleVehicleSubmission flag is off
  // allowIneligibleVehicleSubmission flag is for allowing ineligible vehicles to be submitted for ICO assigned fury funnel
  else if (autocheckIsIneligible() && answerState.other[QUESTION_ARE_QUESTIONS_VERIFIED].value === 1 && !allowIneligibleVehicleSubmission) {
    trackOfferIneligible(answerState.other);
    onOfferSubmitError({ errors: [500000] });
  } else {
    submitOffer(allowIneligibleVehicleSubmission)
      .then((res) => {
        onOfferSubmitSuccess(res, disposalOptions);
      })
      .catch((e) => {
        if (e.errors) {
          if (onValidateResponse(e.errors)) {
            EventBus.publish('OfferSubmitError', e);
          } else if (e.errors.includes(400123)) {
            EventBus.publish('OfferInProgress', e.data);
          } else {
            onOfferSubmitError(e, clearStateOnFailure);
          }
        } else {
          onOfferSubmitFailure(e, clearStateOnFailure);
        }
      });
  }
};

const onValidateResponse = (errors) => {
  let offerError = undefined;
  if (errors.includes(400082)) {
    validationState.fields.contactInfo.add('email');
    offerError = 400092;
  }
  if (errors.includes(400084)) {
    validationState.fields.contactInfo.add('phone');
    offerError = 400092;
  }
  if (errors.includes(400080)) {
    validationState.fields.contactInfo.add('firstName');
    offerError = 400092;
  }
  if (errors.includes(400081)) {
    validationState.fields.contactInfo.add('lastName');
    offerError = 400092;
  }
  if (errors.includes(400020)) {
    validationState.fields.contactInfo.add('zip');
    offerError = 400092;
  }
  offerState.offerError = offerError ?? errors[0];
  return offerError === 400092;
};

export const onOfferSubmitSuccess = (res, disposalOptions: disposalOptions) => {
  const { isKelleyBuysYourCar, isInstantCashOffer, existingKbycOfferId, existingOfferId } = disposalOptions;
  let emittedResponse = {
    ...res,
    offerId: isInstantCashOffer ? res.offerId : existingOfferId,
    kbycOfferId: isKelleyBuysYourCar ? res.offerId : existingKbycOfferId,
  };
  EventBus.publish('OfferSubmitSuccess', emittedResponse);

  onOfferFetchSuccess(res, false);
  postOfferCompleteCobranding(res);
};

export const onOfferSubmitError = (error, clearStateOnFailure = true) => {
  EventBus.publish('OfferSubmitError', error);

  // clear answers in session storage and clear stores if not SC
  if (clearStateOnFailure) {
    console.log('Clearing saved answers and resetting stores on offer submission error');
    answerActions.clearAnswers();
    resetAll();
  }
};

export const onOfferSubmitFailure = (error, clearStateOnFailure = true) => {
  EventBus.publish('OfferSubmitFailure', error);

  // clear answers in session storage and clear stores if not SC
  if (clearStateOnFailure == true) {
    console.log('Clearing saved answers and resetting stores on offer submission error');
    answerActions.clearAnswers();
    resetAll();
  }
};

export const onFireDREvent = (buttonLabel: string = null): void => {
  const { isDrOffer, status } = offerState;

  if (isDrOffer && status != 'CLEARED') {
    onFireDRErrorEvent('offer was deemed not eligible.');
  } else {
    onFireDRSuccessEvent();
  }

  if (buttonLabel) {
    trackOfferCertificateHeader(buttonLabel);
  }
};

export const onFireDRSuccessEvent = (): void => {
  const now = new Date();
  const { dealerBoostAmount, originalExpirationDate, offerId, status, amount, potentialTaxSaving, primaryBodyStyle } = offerState;
  const { email, firstName, lastName, phone, zip } = customerState;
  const { trimId, make, mileage, model, trim, year, vin } = offerVehicleState;
  const payload = {
    type: 'EXTERNAL_TRADE_COMPLETED',
    payload: {
      KBBVehicleId: trimId,
      email,
      firstName,
      lastName,
      make,
      markupAmount: dealerBoostAmount,
      mileage,
      model,
      offerDate: now.toISOString(),
      offerExpirationDate: toISOStringOrNull(originalExpirationDate),
      offerId,
      phone,
      status,
      tivSource: 'ICO',
      trim,
      value: amount,
      year,
      zip,
      vin,
      potentialTaxSaving,
      primaryBodyStyle,
    },
  };

  EventBus.publish('ExternalTradeCompleted', payload);
};

export const onFireDRErrorEvent = (reason): void => {
  const { yearId, makeId, modelId, trimId, vin } = offerVehicleState;

  const payload = {
    type: 'External_Trade_Error', //
    description: `Vehicle is not eligible${reason ? ` because ${reason}` : ''}`,
    year: yearId,
    make: makeId,
    model: modelId,
    trim: trimId,
    vin,
  };

  EventBus.publish('ExternalTradeError', payload);
};

export const postOfferCompleteCobranding = (offer): void => {
  const { offerCode } = pageParamsState;
  const prospect = offer && offer.prospect;
  const isCobrandingOrDr = offerCode.toUpperCase() === 'OC' || offerCode.toUpperCase() === 'WC' || offerCode.toUpperCase() === 'TC' || offerCode.toUpperCase() === 'D';
  if (!(isCobrandingOrDr && offer)) {
    return;
  }
  const markupAmount = calculateDealerBoost(offer.markupAmounts);
  const now = new Date();
  const payload = prospect && {
    type: 'EXTERNAL_TRADE_ADDED',
    payload: {
      KBBVehicleId: prospect.vehicle.trimId,
      email: prospect.contactInfo.email,
      firstName: prospect.contactInfo.firstName,
      lastName: prospect.contactInfo.lastName,
      vin: prospect.vehicle.vin || '',
      make: prospect.vehicle.vehicleDescription.make,
      markupAmount,
      mileage: prospect.mileage,
      model: prospect.vehicle.vehicleDescription.model,
      offerDate: now.toISOString(),
      offerExpirationDate: offer.expirationDate,
      offerId: offer.offerId,
      phone: prospect.contactInfo.phone,
      status: offer.status,
      tivSource: 'ICO',
      trim: prospect.vehicle.vehicleDescription.trim,
      value: offer.amount,
      year: prospect.vehicle.vehicleDescription.year,
      zip: prospect.contactInfo.zip,
      primaryBodyStyle: offer.primaryBodyStyle,
    },
  };

  EventBus.publish('PostOfferCompleteCobrandingEvent', payload);
};

export const isNeedsReviewOffer = () => {
  const { isOfferUnderReview, isExpired } = offerState;
  return isOfferUnderReview && !isExpired;
};

const actions = {
  onOfferFetch,
  onOfferFetchSuccess,
  onOfferFetchFail,
  onOfferSet,
  onOfferSubmit,
  onOfferStatusCheck,
  setOfferCertificateDisplayFlags,
  onFireDREvent,
  onFireDRSuccessEvent,
  onFireDRErrorEvent,
  postOfferCompleteCobranding,
  onUpdateOfferIneligible,
  onInProgressOfferFetch,
  setMinimalCertificateDisplayFlags,
};

export { actions };
export default actions;
